import { request } from './request'
import { normalizeAttendanceFaceApiBaseUrl } from './attendanceFaceBase'

const ATTENDANCE_FACE_SERVER_TARGET = normalizeAttendanceFaceApiBaseUrl(
  process.env.VUE_APP_ATTENDANCE_FACE_SERVER_BASE
)
const ATTENDANCE_FACE_IMAGE_SERVER_TARGET = String(
  process.env.VUE_APP_ATTENDANCE_FACE_IMAGE_BASE || 'http://36.137.177.118:8089'
).trim().replace(/\/+$/, '')

function joinAttendanceFaceUrl(base, path) {
  if (!base) return path
  if (!path) return base
  if (base.endsWith('/') && path.startsWith('/')) return `${base.slice(0, -1)}${path}`
  if (!base.endsWith('/') && !path.startsWith('/')) return `${base}/${path}`
  return `${base}${path}`
}

function extractAttendanceFaceError(payload, fallback = 'Request failed') {
  if (typeof payload?.error === 'string' && payload.error) return payload.error
  if (typeof payload?.message === 'string' && payload.message) return payload.message
  if (typeof payload?.msg === 'string' && payload.msg) return payload.msg
  if (typeof payload?.detail === 'string' && payload.detail) return payload.detail
  return fallback
}

async function attendanceFaceRequest(path, options = {}) {
  return request(path, {
    ...options,
    baseURL: ATTENDANCE_FACE_SERVER_TARGET,
    skipAuth: true,
    skip401Redirect: true,
    forceChallenge: true
  })
}

function ensureAttendanceFaceSuccess(payload, fallback) {
  if (payload?.success === false || payload?.error) {
    const error = new Error(extractAttendanceFaceError(payload, fallback))
    error.data = payload
    throw error
  }
  return payload
}

function normalizeAttendanceFaceResponse(payload = {}) {
  return {
    ...payload,
    imageUrl: payload?.image_url ?? payload?.imageUrl ?? '',
    faceId: payload?.face_id ?? payload?.faceId ?? '',
    devicePushed: payload?.device_pushed ?? payload?.devicePushed,
    successCount: Number(payload?.successCount ?? payload?.success_count ?? 0),
    failCount: Number(payload?.failCount ?? payload?.fail_count ?? 0),
    totalDevices: Number(payload?.totalDevices ?? payload?.total_devices ?? 0)
  }
}

function buildAttendanceRegisterData(payload = {}) {
  const employeeNumber = String(payload.employeeNumber ?? payload.user_id ?? payload.userId ?? '').trim()
  const faceurl = String(payload.faceurl ?? payload.faceUrl ?? payload.image_url ?? payload.imageUrl ?? '').trim()
  const replace = Number(payload.replace ?? 0) === 1 ? 1 : 0
  const name = String(payload.name ?? employeeNumber ?? 'student').trim() || employeeNumber || 'student'

  return {
    employeeNumber,
    name,
    faceurl,
    replace
  }
}

export function getAttendanceFaceServerTarget() {
  return ATTENDANCE_FACE_SERVER_TARGET
}

export function getAttendanceFaceImageServerTarget() {
  return ATTENDANCE_FACE_IMAGE_SERVER_TARGET
}

export function buildAttendanceFaceImageUrl(path) {
  const normalizedPath = String(path || '').trim()
  if (!normalizedPath) return ''
  if (/^https?:\/\//i.test(normalizedPath)) return normalizedPath
  return joinAttendanceFaceUrl(ATTENDANCE_FACE_IMAGE_SERVER_TARGET, `/${normalizedPath.replace(/^\/+/, '')}`)
}

export async function uploadAttendanceFaceImage(file) {
  const formData = new FormData()
  formData.append('file', file)

  const response = await attendanceFaceRequest('/v1/upload/face-image', {
    method: 'POST',
    body: formData
  })

  return normalizeAttendanceFaceResponse(
    ensureAttendanceFaceSuccess(response, 'Upload attendance face image failed')
  )
}

export async function registerAttendanceFace(payload = {}) {
  const data = buildAttendanceRegisterData(payload)
  if (!data.employeeNumber) {
    throw new Error('Missing employeeNumber for face registration')
  }
  if (!data.faceurl) {
    throw new Error('Missing faceurl for face registration')
  }

  const response = await attendanceFaceRequest('/v1/person/register', {
    method: 'POST',
    body: {
      data
    }
  })

  if (typeof response?.code === 'number' && response.code !== 0) {
    const error = new Error(extractAttendanceFaceError(response, 'Register attendance face failed'))
    error.data = response
    throw error
  }

  return normalizeAttendanceFaceResponse(
    ensureAttendanceFaceSuccess(response, 'Register attendance face failed')
  )
}

export async function deleteAttendanceFace(payload = {}) {
  const employeeNumber = String(payload.employeeNumber ?? payload.user_id ?? payload.userId ?? '').trim()
  if (!employeeNumber) {
    throw new Error('Missing employeeNumber for face deletion')
  }

  const response = await attendanceFaceRequest('/v1/person/delete/all', {
    method: 'POST',
    body: {
      data: {
        employeeNumber
      }
    }
  })

  if (typeof response?.code === 'number' && response.code !== 0) {
    const error = new Error(extractAttendanceFaceError(response, 'Delete attendance face failed'))
    error.data = response
    throw error
  }

  return normalizeAttendanceFaceResponse(
    ensureAttendanceFaceSuccess(response, 'Delete attendance face failed')
  )
}
